/**
 * The HUE SCENE picker (2026-10-23, OPBridge S331's ask "Hue scenes on a pad or a timeline cue"): the Hue app's own
 * scenes, room by room, each drawn in its own colours (the swatch OPBridge sends, one colour per light), so a scene
 * is picked by how it looks, not by its name alone. Used by the Inspector's Hue Scene button (`HueSceneEditor`) and
 * the timeline editor's chooser and browser (`HueSceneList`). Data and firing: `lighting/hueScenes.ts`.
 */
import { createMemo, createSignal, For, onCleanup, onMount, Show } from 'solid-js';
import {
  ensureAllHueScenes, findHueScene, hueListSummary, hueRefOf, hueSceneEntries, hueSceneLabel, hueSwatchCss, prepareHueScene,
  refreshHueScenes, watchHueScenes, type HueSceneEntry, type HueSceneRef,
} from '../lighting/hueScenes.ts';
import { bridges, getConnection, resolveBridge } from '../opbridge/store.ts';
import { editItem } from '../layout/store.ts';
import type { ButtonItem } from '../layout/projectStore.ts';
import { Tip, TipRow } from './Tip.tsx';

/** Above this many scenes the list gets a filter box. */
const FILTER_FROM = 10;

/** The scenes, room by room. `selected` is ringed; a pick calls `onPick`. Reads every connected computer's list when
 *  it opens. */
export function HueSceneList(props: { selected?: Pick<HueSceneRef, 'bridge' | 'scene'> | undefined; onPick: (e: HueSceneEntry) => void; testid?: string }) {
  onMount(() => ensureAllHueScenes());
  const [filter, setFilter] = createSignal('');
  const entries = () => hueSceneEntries();
  const groups = createMemo(() => {
    const q = filter().trim().toLowerCase();
    const out = new Map<string, { name: string; kind: string; list: HueSceneEntry[] }>();
    for (const e of entries()) {
      if (q && !`${e.scene.name} ${e.group.name}`.toLowerCase().includes(q)) continue;
      const key = `${e.bridge}|${e.group.id}`;
      const g = out.get(key) ?? { name: e.group.name, kind: e.group.kind, list: [] };
      g.list.push(e);
      out.set(key, g);
    }
    return [...out.values()];
  });
  const sum = () => hueListSummary();
  const isSel = (e: HueSceneEntry) => !!props.selected && props.selected.bridge === e.bridge && props.selected.scene === e.scene.id;
  return (
    <div class="hue-pick" data-testid={props.testid}>
      <Show when={entries().length > FILTER_FROM}>
        <input class="hue-pick-filter" type="search" placeholder="Find a scene" aria-label="Find a scene" value={filter()}
          onInput={(e) => setFilter(e.currentTarget.value)} onKeyDown={(e) => e.stopPropagation()} />
      </Show>
      <Show when={entries().length > 0} fallback={
        <div class="hue-pick-empty" data-state={sum().state}>
          {sum().state === 'reading' ? 'Reading the Hue scenes...' : sum().detail ?? 'No Hue scenes.'}
          <Show when={sum().state !== 'reading' && sum().state !== 'nobridge'}>
            <button type="button" class="hue-pick-again" onClick={() => { for (const b of bridges()) if (getConnection(b.id)?.status() === 'connected') void refreshHueScenes(b.id); }}>Read again</button>
          </Show>
        </div>
      }>
        <For each={groups()} fallback={<div class="hue-pick-empty">No scene matches "{filter()}".</div>}>
          {(g) => (
            <div class="hue-pick-group">
              <div class="hue-pick-room">{g.name}<em>{g.kind === 'zone' ? 'zone' : 'room'}, {g.list.length} scene{g.list.length === 1 ? '' : 's'}</em></div>
              <div class="hue-pick-scenes">
                <For each={g.list}>
                  {(e) => (
                    <button type="button" class="hue-pick-scene" aria-pressed={isSel(e)} data-scene={e.scene.id}
                      title={`${hueSceneLabel({ group: e.group.name, name: e.scene.name, scene: e.scene.id })}${e.scene.moving ? ' (moves)' : ''}${e.scene.active ? ' - on now' : ''}`}
                      onClick={() => props.onPick(e)}>
                      <i class="hue-pick-sw" style={{ background: hueSwatchCss(e.scene.swatch) ?? '#2a2b2e' }} />
                      <span class="hue-pick-name"><span>{e.scene.name}</span><Show when={e.scene.active}><b class="hue-pick-on">on</b></Show></span>
                      <Show when={e.scene.moving}><em>moves</em></Show>
                    </button>
                  )}
                </For>
              </div>
            </div>
          )}
        </For>
      </Show>
    </div>
  );
}

/** The Inspector's editor for a Hue Scene button: pick the scene; the button takes its name and, never aimed before,
 *  the computer that holds the Hue bridge; its named action is registered at once so the first tap is one request. */
export function HueSceneEditor(props: { item: ButtonItem }) {
  onMount(() => { const release = watchHueScenes(); onCleanup(release); });
  const ref = () => props.item.hueScene;
  const chosen = () => findHueScene(ref());
  const aimedRow = (): string => { const f = props.item.bridgeIds?.[0]; return f ? (resolveBridge(f)?.id ?? f) : ''; };
  const missing = () => !!ref()?.scene && !chosen() && (hueListSummary().state === 'ok' || hueListSummary().state === 'none');
  function choose(e: HueSceneEntry): void {
    const next = hueRefOf(e);
    const patch: { hueScene: HueSceneRef; name?: string; bridgeIds?: string[] } = { hueScene: next };
    // An unnamed button, or one still named after its previous scene, takes the scene's name.
    const name = (props.item.name ?? '').trim();
    if (!name || name === (ref()?.name ?? '').trim()) patch.name = e.scene.name;
    if (props.item.bridgeIds === undefined) patch.bridgeIds = [bridges().find((b) => b.id === e.rowId)?.nodeId ?? e.rowId];
    editItem(props.item.id, patch);
    void prepareHueScene(aimedRow() || e.rowId, next);
  }
  return (
    <div class="inspector-zone" data-hue-scene-editor="true">
      <div class="inspector-field">
        <span>Scene</span>
        <span class="hue-pick-current">{chosen() ? hueSceneLabel(hueRefOf(chosen()!)) : ref()?.scene ? hueSceneLabel(ref()!) : 'Pick one below'}</span>
      </div>
      <HueSceneList selected={ref()} onPick={choose} testid="hue-scene-picker" />
      <Show when={missing()}>
        <span class="inspector-hint">"{hueSceneLabel(ref()!)}" is no longer in the Hue app: pick another.</span>
      </Show>
      <Show when={!ref()?.scene}>
        <span class="inspector-hint">No scene chosen - this button does nothing yet.</span>
      </Show>
      <TipRow label="What a tap does" text="A tap recalls it: every light in that room changes at once, as in the Hue app. It lights up while its scene is on." />
    </div>
  );
}
